import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSettings } from "../lib/settings";
import { WaIcon } from "./WhatsAppButton";

export function MobileMenu({ nav }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { settings } = useSettings();
  const wa = (settings.whatsapp_number || "").replace(/\D/g, "");
  const tel = (settings.shop_phone || "").replace(/[^\d+]/g, "");

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <div className="md:hidden flex items-center gap-2">
      {tel && (
        <a
          href={`tel:${tel}`}
          data-testid="header-call-btn"
          aria-label="Call us"
          className="w-11 h-11 rounded-full bg-primary/10 text-primary flex items-center justify-center active:scale-95 transition-transform"
        >
          <Phone className="w-5 h-5" strokeWidth={2} />
        </a>
      )}
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="mobile-menu-btn"
        aria-label="Open menu"
        aria-expanded={open}
        className="w-11 h-11 rounded-full border border-border bg-white text-secondary flex items-center justify-center active:scale-95 transition-transform"
      >
        <Menu className="w-5 h-5" strokeWidth={2} />
      </button>

      {createPortal(
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm"
            />
            <motion.aside
              key="panel"
              data-testid="mobile-menu-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 320, damping: 32 }}
              className="fixed top-0 right-0 bottom-0 z-[70] w-[84vw] max-w-sm bg-white shadow-2xl flex flex-col"
            >
              <div className="h-20 px-5 flex items-center justify-between border-b border-border">
                <div className="font-heading text-lg font-bold">{settings.company_name}</div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-menu-close"
                  aria-label="Close menu"
                  className="w-11 h-11 rounded-full border border-border flex items-center justify-center text-secondary"
                >
                  <X className="w-5 h-5" strokeWidth={2} />
                </button>
              </div>
              <nav className="flex-1 overflow-y-auto px-3 py-4">
                {nav.map((n, i) => (
                  <motion.div
                    key={n.to}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 + i * 0.05 }}
                  >
                    <Link
                      to={n.to}
                      data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                      className={`flex items-center justify-between px-4 py-4 rounded-xl font-heading text-xl font-semibold ${
                        location.pathname === n.to ? "bg-primary/10 text-primary" : "text-secondary active:bg-muted"
                      }`}
                    >
                      {n.label}
                      <ArrowRight className="w-5 h-5 opacity-50" strokeWidth={2} />
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="p-4 border-t border-border space-y-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
                <Link
                  to="/contact"
                  data-testid="mobile-menu-quote"
                  className="flex items-center justify-center gap-2 w-full bg-primary text-white font-semibold rounded-full py-3.5"
                >
                  Get Free Quote <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </Link>
                <div className="grid grid-cols-2 gap-3">
                  {tel && (
                    <a href={`tel:${tel}`} data-testid="mobile-menu-call" className="flex items-center justify-center gap-2 border border-border rounded-full py-3 text-sm font-semibold text-secondary">
                      <Phone className="w-4 h-4 text-primary" strokeWidth={2} /> Call
                    </a>
                  )}
                  {wa && (
                    <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" data-testid="mobile-menu-whatsapp" className="flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-3 text-sm font-semibold">
                      <WaIcon className="w-4 h-4" /> WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>,
      document.body)}
    </div>
  );
}
